import { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { isApplePlatform, isTauri } from '@/lib/platform';

import { useShortcutsDialog } from './shortcuts-dialog-store';
import { keyLabel, type Shortcut, type ShortcutScope, SHORTCUTS } from './shortcuts';

const GROUPS: readonly { scope: ShortcutScope; title: string }[] = [
  { scope: 'everywhere', title: 'General' },
  { scope: 'desktop', title: 'Desktop app' },
  { scope: 'logbook', title: 'Logbook entries' },
];

function Keys({ shortcut, apple }: { shortcut: Shortcut; apple: boolean }) {
  return (
    <span className="flex items-center gap-1">
      {shortcut.keys.map((token, index) => (
        <span key={`${token}-${String(index)}`} className="flex items-center gap-1">
          {index > 0 && shortcut.sequence === true ? (
            <span className="text-muted-foreground text-xs">then</span>
          ) : null}
          <kbd className="border-border bg-surface-sunken rounded-control min-w-6 border px-1.5 py-0.5 text-center font-mono text-xs">
            {keyLabel(token, apple)}
          </kbd>
        </span>
      ))}
    </span>
  );
}

/** Opened with `?`, or Help → Keyboard Shortcuts in the desktop app. */
export function ShortcutsDialog() {
  const open = useShortcutsDialog((state) => state.open);
  const ref = useRef<HTMLDialogElement>(null);
  const apple = isApplePlatform();
  const desktop = isTauri();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) {
      return;
    }
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="shortcuts-title"
      onClose={() => {
        useShortcutsDialog.setState({ open: false });
      }}
      className="border-border bg-surface text-foreground rounded-container backdrop:bg-foreground/40 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-md overflow-y-auto border p-0 shadow-lg print:hidden"
    >
      <div className="p-5 sm:p-6">
        <h2 id="shortcuts-title" className="text-base font-semibold tracking-tight">
          Keyboard shortcuts
        </h2>
        {GROUPS.filter(({ scope }) => scope !== 'desktop' || desktop).map(({ scope, title }) => (
          <section key={scope} className="mt-4">
            <h3 className="text-muted-foreground text-xs font-medium">{title}</h3>
            <ul className="mt-1.5 space-y-1.5">
              {SHORTCUTS.filter((shortcut) => shortcut.scope === scope).map((shortcut) => (
                <li
                  key={`${shortcut.label}-${shortcut.keys.join('')}`}
                  className="flex items-center justify-between gap-4 text-sm"
                >
                  <span>{shortcut.label}</span>
                  <Keys shortcut={shortcut} apple={apple} />
                </li>
              ))}
            </ul>
          </section>
        ))}
        <div className="mt-5 flex justify-end">
          <Button
            variant="ghost"
            onClick={() => {
              useShortcutsDialog.setState({ open: false });
            }}
          >
            Close
          </Button>
        </div>
      </div>
    </dialog>
  );
}
